import React, { useEffect, useRef } from 'react';
import {
  FormControl,
  FormControlProps,
  FormLabel,
  forwardRef,
  HStack,
  Switch,
  SwitchProps,
  useMergeRefs,
} from '@chakra-ui/react';
import { FormLabelPropsWithTestId } from 'types/general.types';
import { HelpTooltip, HelpTooltipProps } from 'app/components/HelpTooltip';

interface FormTooltipPropsWithTestId extends HelpTooltipProps {
  'data-testid'?: string;
}

export interface SwitchFieldProps extends SwitchProps {
  label: string;
  labelProps?: FormLabelPropsWithTestId;
  tooltipProps?: FormTooltipPropsWithTestId;
  className?: string;
  formControlProps?: FormControlProps;
}

export const SwitchField = forwardRef<SwitchFieldProps, 'input'>(
  (props, ref) => {
    const {
      className,
      label,
      labelProps,
      tooltipProps,
      id,
      isRequired,
      isDisabled,
      isInvalid,
      isReadOnly,
      formControlProps,
      isChecked,
      ...rest
    } = props;
    const localRef = useRef<HTMLInputElement>(null);
    const refs = useMergeRefs(localRef, ref);
    useEffect(() => {
      if (localRef?.current) {
        if (isChecked) {
          localRef.current.setAttribute('checked', '');
        } else {
          localRef.current.removeAttribute('checked');
        }
      }
    }, [isChecked]);

    return (
      <FormControl
        className={className}
        display="flex"
        justifyContent="space-between"
        variant="switchControl"
        {...formControlProps}
        {...{ id, isRequired, isDisabled, isInvalid, isReadOnly }}
        pr={tooltipProps ? 0 : 3}
        rounded={4}
      >
        <FormLabel {...labelProps}>{label}</FormLabel>
        <HStack alignItems="center" spacing={2}>
          <Switch
            ref={refs}
            data-testid={label}
            isChecked={isChecked}
            {...rest}
          />
          {tooltipProps && <HelpTooltip {...tooltipProps} />}
        </HStack>
      </FormControl>
    );
  }
);
